import React, { useState } from 'react';

function HowItWorksSection() {
  const [hoveredStep, setHoveredStep] = useState(null);

  const steps = [
    {
      number: '01',
      title: 'Search',
      subtitle: 'Browse Local Therapists',
      description: 'Use our filters to find licensed therapists in your area by specialty, insurance, availability, and more.',
      features: ['Licensed professionals', 'Insurance filtering', 'Location-based search', 'Specialty matching'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      color: 'bg-[#9dacff]'
    },
    {
      number: '02',
      title: 'Find',
      subtitle: 'Discover the Right Fit',
      description: 'Read detailed profiles, credentials, client reviews, and treatment approaches to find your ideal therapist.',
      features: ['Detailed profiles', 'Client reviews', 'Treatment approaches', 'Credentials & certifications'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      color: 'bg-[#254e70]'
    },
    {
      number: '03',
      title: 'Meet',
      subtitle: 'Connect & Begin',
      description: 'Contact your chosen therapist directly to schedule your first session and start your journey to better mental health.',
      features: ['Direct contact information', 'Professional communication', 'Video or in-person options', 'Verified therapist details'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      color: 'bg-[#9dacff] text-white'
    }
  ];

  return (
    <section className="py-16 lg:py-20 bg-gradient-to-br from-[#e4fde1] to-[#e4fde1]/70">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl lg:text-6xl font-semibold text-[#254e70] mb-4">
            Search. Find. <span className="text-[#9dacff]">Meet.</span>
          </h2>
          <p className="text-lg text-[#254e70]/70 max-w-2xl mx-auto">
            Finding the right therapist is simple. Browse profiles, compare options, and book your first session in minutes.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {steps.map((step, index) => (
            <div
              key={index}
              className="group relative"
              onMouseEnter={() => setHoveredStep(index)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              {/* Connection Arrow */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 -right-4 z-10">
                  <svg 
                    className={`w-8 h-8 text-[#9dacff] transition-all duration-300 ${
                      hoveredStep === index ? 'scale-125 text-[#254e70]' : ''
                    }`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              )}

              <div className={`bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/50 transition-all duration-500 hover:shadow-xl ${
                hoveredStep === index ? 'scale-105 bg-white/90' : ''
              }`}>
                {/* Step Number & Icon */}
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg transition-all duration-300 ${step.color} ${
                    hoveredStep === index ? 'scale-110' : ''
                  }`}>
                    {step.number}
                  </div>
                  <div className="w-12 h-12 bg-[#e4fde1]/80 rounded-xl flex items-center justify-center text-[#254e70]">
                    {step.icon}
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-[#254e70] mb-2">
                  {step.title}
                </h3>
                <p className="text-[#9dacff] font-semibold mb-4">
                  {step.subtitle}
                </p>
                <p className="text-[#254e70]/80 leading-relaxed mb-6">
                  {step.description}
                </p>

                {/* Feature List */}
                <ul className="space-y-2">
                  {step.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3 text-[#254e70]/70">
                      <div className="w-2 h-2 bg-[#9dacff] rounded-full flex-shrink-0"></div>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default HowItWorksSection;